import React from 'react'
import {
  Page,
  Document,
  View,
  Text,
  Image,
  Link,
  StyleSheet,
} from '@react-pdf/renderer'
import { MappedAssets } from './AssetsReports.types'

const styles = StyleSheet.create({
  page: {
    padding: 30,
    fontFamily: 'Helvetica',
    fontSize: 9,
  },
  header: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 20,
    borderBottom: '1px solid #ccc',
    paddingBottom: 10,
  },
  headerTitle: {
    fontSize: 24,
    fontWeight: 'bold',
    marginBottom: 0,
  },
  headerSubtitle: {
    fontSize: 14,
    color: '#666',
    textAlign: 'right',
  },
  footer: {
    position: 'absolute',
    bottom: 15,
    right: 30,
    textDecoration: 'none',
  },
  poweredByTextContainer: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
  },
  poweredByText: {
    color: '#5035ed',
    fontSize: 8,
    fontWeight: 'bold',
    marginRight: 5,
    textDecoration: 'none',
  },
  table: {
    width: 'auto',
    marginBottom: 10,
  },
  tableRow: {
    flexDirection: 'row',
    borderBottomWidth: 1,
    borderBottomColor: '#ccc',
    borderBottomStyle: 'solid',
    alignItems: 'center',
    height: 55,
  },
  tableHeader: {
    backgroundColor: '#f0f0f0',
    fontWeight: 'bold',
    textAlign: 'left',
    height: 30,
  },
  tableCell: {
    textAlign: 'left',
    padding: 5,
    fontSize: 9,
  },
  reportSummary: {
    marginBottom: 20,
  },
  summaryRow: {
    flexDirection: 'row',
    marginBottom: 5,
  },
  summaryLabel: {
    width: 150,
    fontWeight: 'bold',
  },
  summaryValue: {
    flex: 1,
  },
})

const COLUMN_WIDTHS = {
  date: 70,
  location: 70,
  patientName: 100,
  surgeon: 110,
  manufacturer: 100,
  description: 110,
  modelNumber: 80,
  lot: 70,
  serial: 80,
  expiration: 70,
  disposition: 70,
  wastedReason: 100,
  assetType: 80,
}

const HEAD_CELLS = [
  { id: 'date', label: 'Date', numeric: true },
  { id: 'location', label: 'Location', numeric: false },
  { id: 'patientName', label: 'Patient', numeric: false },
  { id: 'surgeon', label: 'Surgeon', numeric: false },
  { id: 'manufacturer', label: 'Manufacturer', numeric: false },
  { id: 'description', label: 'Description', numeric: false },
  { id: 'modelNumber', label: 'Model', numeric: false },
  { id: 'lot', label: 'Lot', numeric: false },
  { id: 'serial', label: 'Serial', numeric: false },
  { id: 'expiration', label: 'Expiration', numeric: false },
  { id: 'disposition', label: 'Disposition', numeric: false },
  { id: 'assetType', label: 'Product Type', numeric: false },
]

const getColumnStyle = (columnId: string) => {
  const isNumeric = HEAD_CELLS.find((cell) => cell.id === columnId)?.numeric
  return {
    width: COLUMN_WIDTHS[columnId as keyof typeof COLUMN_WIDTHS],
    textAlign: isNumeric ? ('right' as const) : ('left' as const),
  }
}

const getHeaderColumnStyle = (columnId: string) => {
  return {
    width: COLUMN_WIDTHS[columnId as keyof typeof COLUMN_WIDTHS],
    textAlign: 'left' as const,
  }
}

const rootPath = window.location.origin

const Footer = () => (
  <View style={styles.footer} fixed>
    <Link src="https://www.medgeo.com/">
      <View style={styles.poweredByTextContainer}>
        <Text style={styles.poweredByText}>Powered by</Text>
        <Image src={`${rootPath}/medgeoLogo.png`} style={[{ height: 18 }]} />
      </View>
    </Link>
  </View>
)

const TableChunk = ({
  assets,
  startIndex,
}: {
  assets: MappedAssets[]
  startIndex: number
}) => (
  <View style={styles.table}>
    {/* Table Header */}
    <View style={[styles.tableRow, styles.tableHeader]}>
      {HEAD_CELLS.map((cell) => (
        <Text
          key={cell.id}
          style={[styles.tableCell, getHeaderColumnStyle(cell.id)]}
        >
          {cell.label}
        </Text>
      ))}
    </View>

    {/* Table Rows */}
    {assets?.map((asset, index) => (
      <View
        key={`${asset.serial}-${index}`}
        style={[
          styles.tableRow,
          { backgroundColor: index % 2 === 0 ? '#ffffff' : '#f9f9f9' },
        ]}
      >
        <Text style={[styles.tableCell, getColumnStyle('date')]}>
          {asset.date}
        </Text>
        <Text style={[styles.tableCell, getColumnStyle('location')]}>
          {asset.location}
        </Text>
        <Text style={[styles.tableCell, getColumnStyle('patientName')]}>
          {asset.patientName}
        </Text>

        <Text style={[styles.tableCell, getColumnStyle('surgeon')]}>
          {asset.surgeon}
        </Text>
        <Text style={[styles.tableCell, getColumnStyle('manufacturer')]}>
          {asset.manufacturer}
        </Text>
        <Text style={[styles.tableCell, getColumnStyle('description')]}>
          {asset.productDescription}
        </Text>
        <Text style={[styles.tableCell, getColumnStyle('modelNumber')]}>
          {asset.modelNumber}
        </Text>
        <Text style={[styles.tableCell, getColumnStyle('lot')]}>
          {asset.lot}
        </Text>
        <Text style={[styles.tableCell, getColumnStyle('serial')]}>
          {asset.serial}
        </Text>
        <Text style={[styles.tableCell, getColumnStyle('expiration')]}>
          {asset.expiration}
        </Text>
        <Text style={[styles.tableCell, getColumnStyle('disposition')]}>
          {asset.disposition}
        </Text>
        <Text style={[styles.tableCell, getColumnStyle('assetType')]}>
          {asset.assetType}
        </Text>
      </View>
    ))}
  </View>
)

const AssetsReportPDF = ({ assets }: { assets: MappedAssets[] }) => {
  if (assets?.length === 0) {
    return null
  }

  const firstPageChunkSize = 8
  const subsequentPageChunkSize = 9
  const chunks = []

  if (assets?.length > 0) {
    chunks.push(assets.slice(0, firstPageChunkSize))
  }

  for (
    let i = firstPageChunkSize;
    i < assets?.length;
    i += subsequentPageChunkSize
  ) {
    chunks.push(assets.slice(i, i + subsequentPageChunkSize))
  }

  return (
    <Document>
      {chunks.map((chunk, index) => (
        <Page key={index} size="A4" orientation="landscape" style={styles.page}>
          {index === 0 && (
            <>
              {/* Header */}
              <View style={styles.header}>
                <View>
                  <Text style={styles.headerTitle}>Products Report</Text>
                </View>
                <View>
                  <Text style={styles.headerSubtitle}>
                    {`${assets[0]?.date} - ${assets[assets.length - 1]?.date}`}
                  </Text>
                </View>
              </View>
            </>
          )}

          {/* Table Chunks */}
          <TableChunk
            assets={chunk}
            startIndex={
              index === 0
                ? 0
                : firstPageChunkSize + (index - 1) * subsequentPageChunkSize
            }
          />

          {/* Footer */}
          <Footer />
        </Page>
      ))}
    </Document>
  )
}

export default AssetsReportPDF
